import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  CircularProgress,
  useTheme
} from '@mui/material';
import { 
  CheckCircle, 
  Pending, 
  Flag, 
  AccessTime 
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getTasks } from '../services/taskService';
import { Task } from '../types/task';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await getTasks();
      setTasks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  // Calculate task statistics
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(task => task.completed || task.status === 'completed').length;
  const pendingTasks = totalTasks - completedTasks;
  const highPriorityTasks = tasks.filter(task => task.priority === 'high' && !task.completed).length;
  
  // Calculate upcoming due tasks (due in the next 7 days or marked as due-soon)
  const today = new Date();
  const nextWeek = new Date();
  nextWeek.setDate(today.getDate() + 7);
  
  const upcomingDueTasks = tasks.filter(task => {
    // Include tasks explicitly marked as due-soon
    if (task.status === 'due-soon' && !task.completed) return true;
    
    // Include tasks due in the next 7 days
    if (!task.due_date || task.completed) return false;
    const dueDate = new Date(task.due_date);
    return dueDate >= today && dueDate <= nextWeek;
  }).length;

  // Prepare data for charts
  const statusData = [
    { id: 'Completed', label: 'Completed', value: completedTasks, color: theme.palette.success.main },
    { id: 'Todo', label: 'Todo', value: tasks.filter(task => task.status === 'todo').length, color: theme.palette.info.main },
    { id: 'In Progress', label: 'In Progress', value: tasks.filter(task => task.status === 'in-progress').length, color: theme.palette.warning.main },
  ];

  const priorityData = [
    { id: 'High', label: 'High', value: tasks.filter(task => task.priority === 'high').length, color: theme.palette.error.main },
    { id: 'Medium', label: 'Medium', value: tasks.filter(task => task.priority === 'medium').length, color: theme.palette.warning.main },
    { id: 'Low', label: 'Low', value: tasks.filter(task => task.priority === 'low').length, color: theme.palette.success.main },
  ];

  // Group tasks by creation date for the line chart
  const tasksByDate: Record<string, number> = {};
  tasks.forEach(task => {
    const date = new Date(task.created_at).toISOString().split('T')[0];
    tasksByDate[date] = (tasksByDate[date] || 0) + 1;
  });

  const lineChartData = [
    {
      id: 'Tasks Created',
      color: theme.palette.primary.main,
      data: Object.entries(tasksByDate)
        .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
        .map(([date, count]) => ({
          x: date,
          y: count,
        })),
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 8,
        px: 2,
        borderRadius: '16px',
        backgroundColor: 'rgba(30, 30, 30, 0.6)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 0, 0, 0.2)',
        mt: 4
      }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            mb: 4,
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #00e5ff, #2979ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Dashboard
        </Typography>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                height: '100%',
                background: 'rgba(30, 30, 30, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                borderLeft: `4px solid ${theme.palette.success.main}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircle sx={{ color: theme.palette.success.main, mr: 1 }} />
                    <Typography variant="h6" component="h2">
                      Completed Tasks
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                    {completedTasks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    out of {totalTasks} total tasks
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                height: '100%',
                background: 'rgba(30, 30, 30, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                borderLeft: `4px solid ${theme.palette.info.main}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Pending sx={{ color: theme.palette.info.main, mr: 1 }} />
                    <Typography variant="h6" component="h2">
                      Pending Tasks
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                    {pendingTasks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    awaiting completion
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                height: '100%',
                background: 'rgba(30, 30, 30, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                borderLeft: `4px solid ${theme.palette.error.main}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Flag sx={{ color: theme.palette.error.main, mr: 1 }} />
                    <Typography variant="h6" component="h2">
                      High Priority
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                    {highPriorityTasks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    tasks need attention
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                height: '100%',
                background: 'rgba(30, 30, 30, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                borderLeft: `4px solid ${theme.palette.warning.main}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTime sx={{ color: theme.palette.warning.main, mr: 1 }} />
                    <Typography variant="h6" component="h2">
                      Due Soon
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="p" sx={{ fontWeight: 'bold' }}>
                    {upcomingDueTasks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    tasks due this week
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                height: 400,
                background: 'rgba(30, 30, 30, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}>
                <CardContent sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Task Creation Trend
                  </Typography>
                  <Box sx={{ height: 'calc(100% - 30px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {lineChartData[0].data.length > 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body1" align="center" gutterBottom>
                          Task Creation Summary
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 1, 
                          maxHeight: 280, 
                          overflowY: 'auto',
                          p: 1
                        }}>
                          {lineChartData[0].data.map((item, index) => (
                            <Box 
                              key={index} 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1,
                                borderRadius: '4px',
                                backgroundColor: 'rgba(0, 229, 255, 0.1)',
                                border: '1px solid rgba(0, 229, 255, 0.2)',
                              }}
                            >
                              <Typography variant="body2">
                                {new Date(item.x).toLocaleDateString()}
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Box sx={{ 
                                  height: 10, 
                                  borderRadius: 5,
                                  backgroundColor: theme.palette.primary.main,
                                  width: `${Math.min(item.y * 20, 100)}px`
                                }} />
                                <Typography variant="body2" fontWeight="bold">
                                  {item.y} tasks
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        height: '100%'
                      }}>
                        <Typography variant="body1" color="text.secondary">
                          No task creation data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container spacing={3} direction="column">
              <Grid item xs={12}>
                <motion.div variants={itemVariants}>
                  <Card sx={{ 
                    height: 190,
                    background: 'rgba(30, 30, 30, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  }}>
                    <CardContent sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        Tasks by Status
                      </Typography>
                      <Box sx={{ height: 'calc(100% - 30px)' }}>
                        {totalTasks > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', justifyContent: 'center' }}>
                            {statusData.map((status) => (
                              <Box key={status.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      borderRadius: '50%', 
                                      backgroundColor: status.color 
                                    }} 
                                  />
                                  <Typography variant="body2">{status.label}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      height: 8, 
                                      borderRadius: 4,
                                      backgroundColor: status.color,
                                      width: `${Math.max((status.value / totalTasks) * 100, 5)}px`
                                    }} 
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    {status.value}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: '100%'
                          }}>
                            <Typography variant="body1" color="text.secondary">
                              No status data available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12}>
                <motion.div variants={itemVariants}>
                  <Card sx={{ 
                    height: 190,
                    background: 'rgba(30, 30, 30, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  }}>
                    <CardContent sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        Tasks by Priority
                      </Typography>
                      <Box sx={{ height: 'calc(100% - 30px)' }}>
                        {totalTasks > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', justifyContent: 'center' }}>
                            {priorityData.map((priority) => (
                              <Box key={priority.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      borderRadius: '50%', 
                                      backgroundColor: priority.color 
                                    }} 
                                  />
                                  <Typography variant="body2">{priority.label}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      height: 8, 
                                      borderRadius: 4,
                                      backgroundColor: priority.color,
                                      width: `${Math.max((priority.value / totalTasks) * 100, 5)}px`
                                    }} 
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    {priority.value}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: '100%'
                          }}>
                            <Typography variant="body1" color="text.secondary">
                              No priority data available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};

export default Dashboard;
